/**
 * Sponsor/CRO ePSF setup access (Module = epsf).
 * Study/Site-scoped Sponsor setup only — not Site pharmacy ops or Emergency Unblind.
 */

export const EPSF_SPONSOR_SETUP_PERMISSIONS = [
  { key: "EPSF_IMP_PRODUCT_MANAGE", label: "Manage IMP Products" },
  { key: "EPSF_TREATMENT_SETUP", label: "Manage Treatment Setup" },
  { key: "EPSF_KIT_TREATMENT_MANAGE", label: "Manage Kit Treatment Mapping" },
];

export const EPSF_SPONSOR_SETUP_KEYS = EPSF_SPONSOR_SETUP_PERMISSIONS.map(
  (p) => p.key,
);

const SETUP_KEY_SET = new Set(EPSF_SPONSOR_SETUP_KEYS);

export function isEpsfSponsorSetupKey(key) {
  return SETUP_KEY_SET.has(String(key || "").trim());
}

export function parsePermissionsCsv(csv) {
  return [
    ...new Set(
      String(csv || "")
        .split(/[,;]+/)
        .map((s) => s.trim())
        .filter(Boolean),
    ),
  ];
}

/** Keep only Sponsor setup keys (strip ops/blinding if present on legacy grants). */
export function filterSponsorSetupKeys(keys) {
  return (keys || []).filter((k) => isEpsfSponsorSetupKey(k));
}

/** Nav base keys so Sponsor can open ePSF workspace for IMP Setup only. */
export function withEpsfNavKeys(permissionKeys) {
  const set = new Set(filterSponsorSetupKeys(permissionKeys));
  if (set.size === 0) return [];
  set.add("VIEW_EPSF");
  set.add("EPSF_DASHBOARD");
  return [...set];
}

export function permissionsCsv(keys) {
  return withEpsfNavKeys(keys).join(",");
}

export function epsfLeafId(studyId, siteId, permissionKey) {
  return `epsf|${String(studyId || "").trim().toLowerCase()}|${String(
    siteId || "",
  )
    .trim()
    .toLowerCase()}|${String(permissionKey || "").trim()}`;
}

export function epsfSiteFolderId(studyId, siteId) {
  return `epsf-site|${String(studyId || "").trim().toLowerCase()}|${String(
    siteId || "",
  )
    .trim()
    .toLowerCase()}`;
}

export function epsfStudyFolderId(studyId) {
  return `epsf-study|${String(studyId || "").trim().toLowerCase()}`;
}

/**
 * Unique Study → Site nodes from Assigned Studies report rows / binder grants.
 * Skips empty site (whole-study without site) unless it is the only scope.
 */
export function buildEpsfAssignedScopeTree(assignedRows) {
  const byStudy = new Map();
  (assignedRows || []).forEach((row) => {
    const studyId = String(row?.StudyId || row?.studyId || "").trim();
    if (!studyId) return;
    const siteId = String(row?.SiteId || row?.siteId || "").trim();
    const studyLabel =
      row?.Study ||
      row?.StudyName ||
      row?.label ||
      row?.studyName ||
      studyId;
    const siteLabel =
      (Array.isArray(row?.SiteNames) && row.SiteNames[0]) ||
      row?.Sites ||
      row?.siteName ||
      (siteId ? "Site" : "All sites");

    if (!byStudy.has(studyId.toLowerCase())) {
      byStudy.set(studyId.toLowerCase(), {
        studyId,
        studyLabel: String(studyLabel).trim() || studyId,
        sites: new Map(),
      });
    }
    const slot = byStudy.get(studyId.toLowerCase());
    const siteKey = siteId.toLowerCase();
    if (!slot.sites.has(siteKey)) {
      slot.sites.set(siteKey, {
        siteId,
        siteLabel: String(siteLabel).trim() || (siteId ? "Site" : "All sites"),
      });
    }
  });

  return [...byStudy.values()]
    .map((study) => ({
      studyId: study.studyId,
      studyLabel: study.studyLabel,
      sites: [...study.sites.values()].sort((a, b) =>
        String(a.siteLabel).localeCompare(String(b.siteLabel), undefined, {
          sensitivity: "base",
        }),
      ),
    }))
    .sort((a, b) =>
      String(a.studyLabel).localeCompare(String(b.studyLabel), undefined, {
        sensitivity: "base",
      }),
    );
}

export function buildEpsfCheckboxTreeData(scopeStudies) {
  return (scopeStudies || []).map((study) => ({
    id: epsfStudyFolderId(study.studyId),
    name: study.studyLabel,
    type: "folder",
    children: (study.sites || []).map((site) => ({
      id: epsfSiteFolderId(study.studyId, site.siteId),
      name: site.siteLabel,
      type: "folder",
      children: EPSF_SPONSOR_SETUP_PERMISSIONS.map((perm) => ({
        id: epsfLeafId(study.studyId, site.siteId, perm.key),
        name: perm.label,
        type: "file",
      })),
    })),
  }));
}

/** Module=epsf grant rows → checked leaf (+ folder) ids for CheckboxTreeView. */
export function checkedIdsFromEpsfGrantRows(grantRows, scopeStudies) {
  const leafIds = new Set();
  (grantRows || []).forEach((row) => {
    if (String(row?.module || "").toLowerCase() !== "epsf") return;
    const studyId = String(row?.studyId || "").trim();
    const siteId = String(row?.siteId || "").trim();
    if (!studyId) return;
    filterSponsorSetupKeys(parsePermissionsCsv(row.permissions)).forEach(
      (key) => {
        leafIds.add(epsfLeafId(studyId, siteId, key));
      },
    );
  });

  const checked = new Set(leafIds);
  (scopeStudies || []).forEach((study) => {
    const studyLeaves = [];
    (study.sites || []).forEach((site) => {
      const siteLeaves = EPSF_SPONSOR_SETUP_KEYS.map((key) =>
        epsfLeafId(study.studyId, site.siteId, key),
      );
      studyLeaves.push(...siteLeaves);
      const siteAll = siteLeaves.every((id) => leafIds.has(id));
      const siteAny = siteLeaves.some((id) => leafIds.has(id));
      if (siteAll && siteAny) {
        checked.add(epsfSiteFolderId(study.studyId, site.siteId));
      }
    });
    if (
      studyLeaves.length &&
      studyLeaves.every((id) => leafIds.has(id))
    ) {
      checked.add(epsfStudyFolderId(study.studyId));
    }
  });
  return [...checked];
}

/**
 * Checked leaf ids → Module=epsf document rows (one row per Study/Site with any setup key).
 */
export function buildEpsfDocumentRowsFromCheckedIds(
  checkedIds,
  scopeStudies,
  accessUntilBySite = {},
) {
  const checked = new Set(checkedIds || []);
  const rows = [];
  (scopeStudies || []).forEach((study) => {
    (study.sites || []).forEach((site) => {
      const keys = EPSF_SPONSOR_SETUP_KEYS.filter((key) =>
        checked.has(epsfLeafId(study.studyId, site.siteId, key)),
      );
      if (!keys.length) return;
      const pairKey = `${String(study.studyId).toLowerCase()}|${String(
        site.siteId || "",
      ).toLowerCase()}`;
      rows.push({
        module: "epsf",
        studyId: study.studyId,
        siteId: site.siteId || "",
        category: "",
        scopeType: site.siteId ? "SITE" : "WHOLE_STUDY",
        folderids: "",
        permissions: permissionsCsv(keys),
        accessUntil: accessUntilBySite[pairKey] || "",
      });
    });
  });
  return rows;
}

export function accessUntilMapFromEpsfGrants(grantRows) {
  const map = {};
  (grantRows || []).forEach((row) => {
    if (String(row?.module || "").toLowerCase() !== "epsf") return;
    const studyId = String(row?.studyId || "").trim();
    if (!studyId) return;
    const siteId = String(row?.siteId || "").trim();
    const key = `${studyId.toLowerCase()}|${siteId.toLowerCase()}`;
    if (row.accessUntil) map[key] = row.accessUntil;
  });
  return map;
}
